import { useCallback, useEffect, useMemo, useState } from 'react';

import { Alert, Card, Col, Container, Form, Row, Spinner, Table } from 'react-bootstrap';
import { FaChartLine, FaCoins } from 'react-icons/fa6';
import { useApp } from 'common/AppContext';

const formatTokens = n => (Number(n) || 0).toLocaleString();
const formatUsd = n => `$${(Number(n) || 0).toFixed(4)}`;
const formatUsdShort = n => `$${(Number(n) || 0).toFixed(2)}`;

const currentMonth = () => {

    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

};

/**
 * Sum a list of usage rows into a single totals object. Used to roll up
 * per-month / per-user / per-day buckets after the API has already grouped
 * them by (month/day, user_email, model_id, purpose).
 */
const sumRows = rows => rows.reduce((acc, r) => ({
    cache_read_input_tokens: acc.cache_read_input_tokens + (r.cache_read_input_tokens || 0),
    cache_write_input_tokens: acc.cache_write_input_tokens + (r.cache_write_input_tokens || 0),
    calls: acc.calls + (r.calls || 0),
    cost_usd: acc.cost_usd + (r.cost_usd || 0),
    input_tokens: acc.input_tokens + (r.input_tokens || 0),
    output_tokens: acc.output_tokens + (r.output_tokens || 0)
}), {
    cache_read_input_tokens: 0,
    cache_write_input_tokens: 0,
    calls: 0,
    cost_usd: 0,
    input_tokens: 0,
    output_tokens: 0
});

/**
 * Group rows by a key extractor and roll each bucket up to a totals row.
 * Returns an array of [key, totals], sorted by key descending — newest first.
 */
const groupBy = (rows, keyFn) => {

    const buckets = new Map();

    for (const r of rows) {

        const k = keyFn(r);
        if (!buckets.has(k)) buckets.set(k, []);
        buckets.get(k).push(r);

    }

    return Array.from(buckets.entries())
        .map(([k, rs]) => [k, sumRows(rs)])
        .sort((a, b) => (a[0] < b[0] ? 1 : -1));

};

export const Usage = () => {

    const { api, user } = useApp();
    const isAdmin = !!(user && user.isAdmin);

    const [monthly, setMonthly] = useState([]);
    const [daily, setDaily] = useState([]);
    const [pricing, setPricing] = useState({});
    const [users, setUsers] = useState([]);
    const [selectedMonth, setSelectedMonth] = useState(currentMonth());
    const [selectedUser, setSelectedUser] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchMonthly = useCallback(() => api.get('/api/usage/monthly?months=12')
        .then(d => setMonthly(d?.rows || [])), [api]);

    const fetchDaily = useCallback(month => api.get(`/api/usage/daily?month=${month}`)
        .then(d => setDaily(d?.rows || [])), [api]);

    // Initial load: monthly rollup, user list, and pricing table. These don't
    // change with the selectedMonth picker, so they live in a separate effect
    // that re-runs only when the api client changes.
    useEffect(() => {

        setLoading(true);
        setError(null);

        Promise.all([
            fetchMonthly(),
            api.get('/api/usage/users').then(d => setUsers(d?.users || [])),
            api.get('/api/usage/pricing').then(d => setPricing(d?.pricing || {}))
        ])
            .catch(e => setError(e?.err || e?.message || String(e)))
            .finally(() => setLoading(false));

    }, [api, fetchMonthly]);

    // Daily breakdown re-fetches whenever the user picks a different month.
    useEffect(() => {

        fetchDaily(selectedMonth).catch(e => setError(e?.err || e?.message || String(e)));

    }, [fetchDaily, selectedMonth]);

    const filteredMonthly = useMemo(
        () => (selectedUser ? monthly.filter(r => r.user_email === selectedUser) : monthly),
        [monthly, selectedUser]
    );

    const filteredDaily = useMemo(
        () => (selectedUser ? daily.filter(r => r.user_email === selectedUser) : daily),
        [daily, selectedUser]
    );

    const monthRows = useMemo(() => groupBy(filteredMonthly, r => r.month), [filteredMonthly]);
    const userRows = useMemo(() => groupBy(filteredMonthly, r => r.user_email || '(unknown)'), [filteredMonthly]);
    const dayRows = useMemo(() => groupBy(filteredDaily, r => r.date), [filteredDaily]);
    const grandTotal = useMemo(() => sumRows(filteredMonthly), [filteredMonthly]);

    const usedModels = useMemo(() => {

        const set = new Set([...monthly, ...daily].map(r => r.model_id).filter(Boolean));
        return Array.from(set).sort();

    }, [monthly, daily]);

    return (

        <Container className="py-4">

            <div className="d-flex align-items-center gap-2 mb-1">
                <FaChartLine className="text-primary" size={22} />
                <h3 className="m-0">
                    Token Usage & Cost
                </h3>
            </div>
            <p className="text-muted">
                Bedrock token consumption per user, with estimated AWS cost. Rolls up daily for the selected month
                and across the last 12 months.
            </p>

            {error && (
                <Alert variant="danger">
                    {error}
                </Alert>
            )}

            <Row className="g-2 mt-1 mb-3 align-items-end">

                {isAdmin && (
                    <Col md={3}>
                        <Form.Group controlId="usage-user">
                            <Form.Label>
                                User
                            </Form.Label>
                            <Form.Select onChange={e => setSelectedUser(e.target.value)} value={selectedUser}>
                                <option value="">
                                    All users
                                </option>
                                {users.map(u => (
                                    <option key={u} value={u}>
                                        {u}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                )}

                <Col md={3}>
                    <Form.Group controlId="usage-month">
                        <Form.Label>
                            Daily breakdown for
                        </Form.Label>
                        <Form.Control
                            onChange={e => setSelectedMonth(e.target.value)}
                            type="month"
                            value={selectedMonth}
                        />
                    </Form.Group>
                </Col>

            </Row>

            {loading ? <Spinner animation="border" /> : (

                <>

                    <Row className="g-3 mb-3">
                        <Col md={4}>
                            <SummaryCard label="Total calls" value={formatTokens(grandTotal.calls)} />
                        </Col>
                        <Col md={4}>
                            <SummaryCard
                                value={formatTokens(
                                    grandTotal.input_tokens
                                    + grandTotal.output_tokens
                                    + grandTotal.cache_read_input_tokens
                                    + grandTotal.cache_write_input_tokens
                                )}
                                label="Total tokens"
                            />
                        </Col>
                        <Col md={4}>
                            <SummaryCard label="Estimated cost (12 mo.)" value={formatUsdShort(grandTotal.cost_usd)} />
                        </Col>
                    </Row>

                    <Card className="border-0 shadow-sm mb-4">
                        <Card.Body>
                            <Card.Title className="h5">
                                Monthly Totals
                            </Card.Title>
                            <p className="text-muted small mb-2">
                                {selectedUser ? `Filtered to ${selectedUser}.` : 'All users combined.'}
                            </p>
                            <UsageTable
                                emptyText="No usage recorded yet."
                                firstColLabel="Month"
                                rows={monthRows}
                            />
                        </Card.Body>
                    </Card>

                    {isAdmin && !selectedUser && (
                        <Card className="border-0 shadow-sm mb-4">
                            <Card.Body>
                                <Card.Title className="h5">
                                    Per-User Totals (last 12 months)
                                </Card.Title>
                                <UsageTable
                                    emptyText="No users yet."
                                    firstColLabel="User"
                                    rows={userRows}
                                />
                            </Card.Body>
                        </Card>
                    )}

                    <Card className="border-0 shadow-sm mb-4">
                        <Card.Body>
                            <Card.Title className="h5">
                                Daily breakdown —
                                {' '}
                                {selectedMonth}
                            </Card.Title>
                            <UsageTable
                                emptyText="No usage in this month."
                                firstColLabel="Date"
                                rows={dayRows}
                            />
                        </Card.Body>
                    </Card>

                    <Card className="border-0 shadow-sm">
                        <Card.Body>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <FaCoins className="text-secondary" />
                                <Card.Title className="h5 m-0">
                                    Pricing reference (USD per 1M tokens)
                                </Card.Title>
                            </div>
                            <PricingTable models={usedModels} pricing={pricing} />
                            <div className="text-muted small mt-2">
                                Costs above are best-effort estimates from on-demand Bedrock list prices and do
                                not include provisioned-throughput, cross-region routing, or volume discounts.
                            </div>
                        </Card.Body>
                    </Card>

                </>

            )}

        </Container>

    );

};

const SummaryCard = ({ label, value }) => (
    <Card className="border-0 shadow-sm h-100">
        <Card.Body>
            <div className="text-muted small text-uppercase" style={{ letterSpacing: 0.5 }}>
                {label}
            </div>
            <div className="h3 mt-1 mb-0">
                {value}
            </div>
        </Card.Body>
    </Card>
);

const UsageTable = ({ emptyText, firstColLabel, rows }) => {

    if (!rows.length) {

        return (
            <div className="text-muted small">
                {emptyText}
            </div>
        );

    }

    return (
        <Table hover responsive className="mb-0" size="sm">
            <thead>
                <tr>
                    <th>
                        {firstColLabel}
                    </th>
                    <th className="text-end">
                        Calls
                    </th>
                    <th className="text-end">
                        Input
                    </th>
                    <th className="text-end">
                        Output
                    </th>
                    <th className="text-end">
                        Cache R / W
                    </th>
                    <th className="text-end">
                        Est. Cost
                    </th>
                </tr>
            </thead>
            <tbody>
                {rows.map(([key, t]) => (
                    <tr key={key}>
                        <td>
                            {key}
                        </td>
                        <td className="text-end">
                            {formatTokens(t.calls)}
                        </td>
                        <td className="text-end">
                            {formatTokens(t.input_tokens)}
                        </td>
                        <td className="text-end">
                            {formatTokens(t.output_tokens)}
                        </td>
                        <td className="text-end text-muted small">
                            {formatTokens(t.cache_read_input_tokens)}
                            {' / '}
                            {formatTokens(t.cache_write_input_tokens)}
                        </td>
                        <td className="text-end fw-semibold">
                            {formatUsd(t.cost_usd)}
                        </td>
                    </tr>
                ))}
            </tbody>
        </Table>
    );

};

const PricingTable = ({ models, pricing }) => {

    const visible = models.length ? models : Object.keys(pricing);

    if (!visible.length) {

        return (
            <div className="text-muted small">
                No pricing data.
            </div>
        );

    }

    return (
        <Table responsive className="mb-0" size="sm">
            <thead>
                <tr>
                    <th>
                        Model
                    </th>
                    <th className="text-end">
                        Input
                    </th>
                    <th className="text-end">
                        Output
                    </th>
                    <th className="text-end">
                        Cache write
                    </th>
                    <th className="text-end">
                        Cache read
                    </th>
                </tr>
            </thead>
            <tbody>
                {visible.map(m => {

                    const r = pricing[m] || {};
                    return (
                        <tr key={m}>
                            <td>
                                <code>
                                    {m}
                                </code>
                            </td>
                            <td className="text-end">
                                {r.input != null ? `$${r.input.toFixed(2)}` : '—'}
                            </td>
                            <td className="text-end">
                                {r.output != null ? `$${r.output.toFixed(2)}` : '—'}
                            </td>
                            <td className="text-end">
                                {r.cacheWrite != null ? `$${r.cacheWrite.toFixed(2)}` : '—'}
                            </td>
                            <td className="text-end">
                                {r.cacheRead != null ? `$${r.cacheRead.toFixed(2)}` : '—'}
                            </td>
                        </tr>
                    );

                })}
            </tbody>
        </Table>
    );

};
